import React from "react";
import styled from "styled-components";
import { motion, useReducedMotion } from "framer-motion";
import { color, ease, font } from "../tokens";
import { Inner, MonoLabel, STATIC_MODE } from "../primitives";

const Wrap = styled.header`
  padding-top: clamp(60px, 10vh, 120px);
  padding-bottom: clamp(80px, 14vh, 160px);
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${font.display};
  /* Condensed display — sized to fill the content width on one line. */
  font-size: clamp(64px, 16vw, 252px);
  font-weight: 400;
  line-height: 0.92;
  letter-spacing: 0.005em;
  text-transform: uppercase;
  color: ${color.ink};
  white-space: nowrap;

  /* Two stacked lines on small screens. */
  @media (max-width: 640px) {
    white-space: normal;
    font-size: clamp(88px, 30vw, 128px);
    line-height: 0.9;
  }
`;

const Statement = styled.p`
  margin: clamp(36px, 6vh, 72px) 0 0;
  font-family: ${font.body};
  font-size: clamp(21px, 2.2vw, 28px);
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.01em;
  max-width: 24em;
  color: ${color.ink};
  text-wrap: pretty;
`;

const StatementMuted = styled.span`
  color: ${color.muted};
`;

const MetaRow = styled.div`
  margin-top: clamp(28px, 5vh, 56px);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const HERO_META = ["Typescript Fullstack", "Senior Consultant", "Sydney"];

const stagger = (reduced: boolean | null, i: number) => ({
  initial: { opacity: 0, y: reduced ? 0 : 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: ease.out },
});

const Hero: React.FC = () => {
  const reduced = useReducedMotion();
  const Block: React.ElementType = STATIC_MODE ? "div" : motion.div;
  const blockProps = (i: number) => (STATIC_MODE ? {} : stagger(reduced, i));
  return (
    <Wrap>
      <Inner>
        <Block {...blockProps(0)}>
          <Name>Elbert Wan</Name>
        </Block>
        <Block {...blockProps(1)}>
          <Statement>
            I build enterprise web, mobile and AI products.{" "}
            <StatementMuted>
              For eight years, across five industries — all of them still in
              production.
            </StatementMuted>
          </Statement>
        </Block>
        <Block {...blockProps(2)}>
          <MetaRow>
            {HERO_META.map((m, i) => (
              <React.Fragment key={m}>
                {i > 0 && <MonoLabel aria-hidden>·</MonoLabel>}
                <MonoLabel>{m}</MonoLabel>
              </React.Fragment>
            ))}
          </MetaRow>
        </Block>
      </Inner>
    </Wrap>
  );
};

export default Hero;
